import React, { Fragment, useEffect, useState } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import {
    StyledPaper,
    StyledTable,
    StyledTableCell,
    StyledTableRow,
    StyledCard,
    StyledCardContent,
} from '../Shifts/StyledTable'; // Custom styled components based on Material-UI or similar
import { TableContainer, TableBody, TableHead } from '@material-ui/core';
import { Button, Input } from 'reactstrap';
import ModalShiftTimePicker from './ModalShiftTimePicker';
import { fileDownload, formatDatetime, getTsDate, notify } from '../../../utils';
import apiClient from '../../../apiClient';
import useUser from '../../../customHooks/useUser';
import useSWR from 'swr';
import { ChoiceEmployee, Team } from '../../../globals/interfaces';
import { Shift } from '../Shifts/types';
import SelectTeam from './SelectTeam';
import ModalManageMembers from './ModalManageMembers';
import ModalConfirmClear from './ModalConfirmClear';


import useTeams from './useTeams';
import useEmployeesData from './useEmployeesData';
import useWeekNavigator from './useWeekNavigator';
import useModal from '../../../customHooks/useModal';
import SearchEmployee from './SearchEmployee';
import { Moment } from 'moment';
import _ from 'lodash';
import ModalConfirmDuplicate from './ModalConfirmDuplicate';
import Loader from 'react-spinners/ScaleLoader';

export interface Employee extends ChoiceEmployee {
    shifts: Record<string, Shift>;// Shifts keyed by date
}

export type HandleShiftSave = (
    startDate: string,
    startTime: string,
    endDate: string,
    endTime: string,
    type: string,
    notes: string
) => void;


const ShiftSchedulerGrid: React.FC<{ activeTab: string; }> = ({
    activeTab
}) => {
    const { user } = useUser();
    const { currentWeek, endOfCurrentWeek, goToPreviousWeek, goToNextWeek, getDaysOfWeek, handleDateChange } = useWeekNavigator();
    const [searchEmployee, setSearchEmployee] = useState('');

    const [memberModified, setMemberModified] = useState(0);
    const { teams, selectedTeamId, setSelectedTeamId, selectedTeam, createUpdateTeam, deleteTeam } = useTeams(activeTab === 'shifts', searchEmployee);
    const { employeesWithShifts, mutateEmployeesWithShifts, employees, isLoading, isValidating } = useEmployeesData(
        activeTab === 'shifts',
        memberModified,
        selectedTeamId,
        user.s_unit,
        formatDatetime(String(currentWeek), true),
        formatDatetime(String(endOfCurrentWeek), true)
    );

    const [selectedDay, setSelectedDay] = useState<string | null>(null);
    const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
    const [initialShiftTimes, setInitialShiftTimes] = useState<{ startTime: string; endTime: string }>({
        startTime: '08:00',
        endTime: '17:00',
    });
    const [existingShiftId, setExistingShiftId] = useState<number>();
    const { modal: modalShift, setModal: setModalShift } = useModal();
    const { modal: modalMembers, setModal: setModalMembers } = useModal();
    const { modal: modalConfirmDuplicate, setModal: setModalConfirmDuplicate } = useModal();
    const { modal: modalConfirmClear, setModal: setModalConfirmClear } = useModal();

    const daysOfWeek = getDaysOfWeek(currentWeek);

    const handleDayClick = (employee: Employee, day: string) => {
        setSelectedEmployee(employee);
        setSelectedDay(day);

        const existingShift = employee.shifts[day];
        if (existingShift) {
            setInitialShiftTimes({
                startTime: moment.utc(existingShift.startDateTime).format('YYYY-MM-DD HH:mm'),
                endTime: moment.utc(existingShift.endDateTime).format('YYYY-MM-DD HH:mm'),
            });
            setExistingShiftId(existingShift.id);
        } else {
            setInitialShiftTimes({
                startTime: `${day} 08:00`,
                endTime: `${day} 17:00`,
            });
            setExistingShiftId(undefined);
        }

        setModalShift(true);
    };

    const getExistingShiftsForDay = (selectedDay: string): Array<Shift> => {
        if (!selectedEmployee || !selectedDay) return [];

        const nextDay = moment(selectedDay).add(1, 'days').format('YYYY-MM-DD');

        return Object.values(selectedEmployee.shifts).filter(shift => {
            if (shift.id === existingShiftId) return false;

            const shiftStart = moment(shift.startDateTime);
            const shiftEnd = moment(shift.endDateTime);

            return (
                shiftStart.isSame(selectedDay, 'day') ||                // Shift starts on the selected day
                shiftEnd.isSame(selectedDay, 'day') ||                  // Shift ends on the selected day
                (shiftStart.isSame(nextDay, 'day') && shiftStart.isBefore(moment(nextDay).endOf('day'))) || // Shift starts on the next day but within the viewable range
                (shiftEnd.isSame(nextDay, 'day') && shiftEnd.isAfter(moment(nextDay).startOf('day')))       // Shift ends on the next day but within the viewable range
            );
        });
    };

    const handleShiftSave = async (data: Shift) => {
        if (!selectedEmployee || !selectedDay) return;

        try {
            await apiClient.post(`/shift`, data);

            notify('Success');

            // const { startDateTime, endDateTime } = data;
            // const updatedEmployees = employeesWithShifts.map(emp =>
            //     emp.id === selectedEmployee.id
            //         ? {
            //             ...emp,
            //             shifts: {
            //                 ...emp.shifts,
            //                 [selectedDay]: { startDateTime, endDateTime },
            //             },
            //         }
            //         : emp
            // );

            mutateEmployeesWithShifts();
            setSelectedDay(null);
            setSelectedEmployee(null);
            setModalShift(false);
        } catch (err) {
            alert(err);
        }
    };

    const exportData = () => {
        const createShiftJsonArray = (employeesWithShifts, daysOfWeek) => {
            return employeesWithShifts.map(employee => {
                const employeeShifts = {
                    employee: `${employee.s_first_name} ${employee.s_last_name}`
                };

                // Iterate over each day and add the shift for that day if it exists
                daysOfWeek.forEach(dayItem => {
                    const shift = employee.shifts[dayItem.day];
                    if (shift) {
                        const startTime = moment.utc(shift.startDateTime).format('HH:mm');
                        const endTime = moment.utc(shift.endDateTime).format('HH:mm');
                        employeeShifts[dayItem.day] = `${startTime} - ${endTime}`;
                    } else {
                        employeeShifts[dayItem.day] = null; // Or leave it undefined if you don't want null values
                    }
                });

                employeeShifts['total hours'] = calculateTotalHours(employee);

                return employeeShifts;
            });
        };
        const data = createShiftJsonArray(employeesWithShifts, daysOfWeek);
        const formatWeek = (date: Moment) => moment(date).format('YYYY-MM-DD');
        fileDownload(data, `${selectedTeam.name} Shifts ${formatWeek(currentWeek)} to ${formatWeek(endOfCurrentWeek)}.csv`);
    }

    const duplicateSchedule = async () => {
        const addWeek = (dateTime: string, format: string) => moment(dateTime).utc().add(1, 'week').format(format);

        const yearFormat = 'YYYY-MM-DD';
        const dateTimeFormat = 'YYYY-MM-DD HH:mm';
        const startDate = addWeek(String(currentWeek), yearFormat);
        const endDate = addWeek(String(endOfCurrentWeek), yearFormat);

        const duplicateShifts: Array<Shift> = [];
        for (const { shifts } of employeesWithShifts) {
            for (const key in shifts) {
                const currentShift = _.cloneDeep(shifts[key]);
                currentShift.startDateTime = addWeek(currentShift.startDateTime, dateTimeFormat);
                currentShift.endDateTime = addWeek(currentShift.endDateTime, dateTimeFormat);
                currentShift.createdAt = getTsDate();
                currentShift.createdBy = user.s_email;
                currentShift.modifiedAt = getTsDate();
                currentShift.modifiedBy = user.s_email;

                delete currentShift.id;

                duplicateShifts.push(currentShift);
            }
        }

        try {
            await apiClient.post(`/shift/duplicate`, {
                shifts: duplicateShifts,
                startDate,
                endDate,
                teamId: selectedTeamId
            });
            goToNextWeek();
            setModalConfirmDuplicate(false);
            notify('Schedule Duplicated');
        } catch (err) {
            alert(err);
        }
    }

    const clearSchedule = async () => {
        const yearFormat = 'YYYY-MM-DD';
        const startDate = moment(currentWeek).format(yearFormat);
        const endDate = moment(endOfCurrentWeek).format(yearFormat);

        try {
            await apiClient.post(`/shift/clear`, {
                startDate,
                endDate,
                teamId: selectedTeamId
            });
            setModalConfirmDuplicate(false);
            notify('Schedule Cleared');
            mutateEmployeesWithShifts();
        } catch (err) {
            alert(err);
        }
    }

    const deleteShift = async (id: number) => {
        try {
            await apiClient.delete(`/shift/${id}`);
            mutateEmployeesWithShifts();
            setModalShift(false);
            notify('Shift Deleted');
        } catch (err) {
            alert(err);
        }
    }

    const calculateTotalHours = (employee: Employee): number => {
        const totalHours = Object.values(employee.shifts).reduce((total, shift) => {
            const startMoment = moment(shift.startDateTime);
            const endMoment = moment(shift.endDateTime);
            const add = endMoment.diff(startMoment, 'hours', true);
            return total + add; // Calculate hours and add to the total
        }, 0);
        return totalHours;
    };

    const totalHours = (employee: Employee) => calculateTotalHours(employee);
    const renderTotalHours = (employee: Employee) => ` (${totalHours(employee)})`;

    const renderDuplicateScheduleButton = () => {
        if (!selectedTeamId || !selectedTeam) return false;

        for (const { shifts } of employeesWithShifts) {
            if (Object.keys(shifts).length > 0) {
                return true;
            }
        }

        return false;
    }

    return (
        <Container>
            <FlexContainer>
                <SelectTeam
                    teams={teams}
                    selectedTeamId={selectedTeamId}
                    setSelectedTeamId={setSelectedTeamId}
                    selectedTeam={selectedTeam}
                    createUpdateTeam={createUpdateTeam}
                    deleteTeam={deleteTeam}
                    setModalMembers={setModalMembers}
                    exportData={exportData}
                />
            </FlexContainer>
            <SpaceBetweenContainer>
                <SearchEmployee
                    employees={employees}
                    searchEmployee={searchEmployee}
                    setSearchEmployee={setSearchEmployee}
                />
                <ControlPanel>
                    <div>
                        {/* <Label>Search by Week</Label> */}
                        <FlexContainer>
                            <Button onClick={goToPreviousWeek}><Icon className={'fa-regular fa-arrow-left'} /> Prev Week</Button>
                            <Button onClick={goToNextWeek}>Next Week <Icon className={'fa-regular fa-arrow-right'} /></Button>
                            {
                                renderDuplicateScheduleButton() && (
                                    <Fragment>
                                        <Button onClick={() => setModalConfirmDuplicate(true)}>Duplicate Schedule for Next week<Icon className={'fa-regular fa-copy pl-2'} /></Button>
                                        <Button onClick={() => setModalConfirmClear(true)}>Clear Schedule<Icon className={'fa-regular fa-delete-left pl-2'} /></Button>
                                    </Fragment>
                                )
                            }
                        </FlexContainer>
                    </div>
                    <div>
                        {/* <Label>Search by Date</Label> */}
                        <WeekDatePicker type="date" onChange={handleDateChange} />
                    </div>
                </ControlPanel>
            </SpaceBetweenContainer>
            <StyledPaper>
                <TableContainer style={{ maxHeight: 'calc(100vh - 520px)' }}>
                    <StyledTable stickyHeader>
                        <TableHead>
                            <StyledTableRow>
                                <StyledTableCell>EMPLOYEE</StyledTableCell>
                                {daysOfWeek.map(item => (
                                    <StyledTableCell key={item.day}>{item.renderDay}</StyledTableCell>
                                ))}
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            {employeesWithShifts.map(employee => (
                                <StyledTableRow key={employee.id}>
                                    <StyledTableCell warning={totalHours(employee) > 40}>
                                        {employee.s_first_name}
                                        {renderTotalHours(employee)}
                                    </StyledTableCell>
                                    {daysOfWeek.map(item => (
                                        <StyledTableCell
                                            key={item.day}
                                            onClick={() => handleDayClick(employee, item.day)}
                                        >
                                            {employee.shifts[item.day] ? (
                                                <StyledCard variant="outlined">
                                                    <StyledCardContent>
                                                        {moment.utc(employee.shifts[item.day].startDateTime).format('HH:mm')} -{' '}
                                                        {moment.utc(employee.shifts[item.day].endDateTime).format('HH:mm')}
                                                    </StyledCardContent>
                                                </StyledCard>
                                            ) : (
                                                <Button color="primary" size="sm">
                                                    Set Shift
                                                </Button>
                                            )}
                                        </StyledTableCell>
                                    ))}
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </StyledTable>
                </TableContainer>

            </StyledPaper>

            <ModalShiftTimePicker
                modal={modalShift && Boolean(selectedDay) && Boolean(selectedEmployee)}
                setModal={setModalShift}
                handleShiftSave={handleShiftSave}
                deleteShift={deleteShift}
                initialStartTime={initialShiftTimes.startTime}
                initialEndTime={initialShiftTimes.endTime}
                selectedTeamId={selectedTeamId}
                selectedDay={selectedDay}
                selectedEmployee={selectedEmployee}
                existingShifts={getExistingShiftsForDay(selectedDay)} // Pass the existing shifts
                existingShiftId={existingShiftId}
            />

            <ModalManageMembers
                modal={modalMembers}
                setModal={setModalMembers}
                employees={employees}
                selectedTeamId={selectedTeamId}
                selectedTeam={selectedTeam}
                setMemberModified={setMemberModified}
                updateEmployeesWithShiftsApi={`/shift?teamId=${selectedTeamId}&startDate=${currentWeek}&endDate=${endOfCurrentWeek}`}
            />

            <ModalConfirmDuplicate
                modal={modalConfirmDuplicate}
                setModal={setModalConfirmDuplicate}
                selectedTeam={selectedTeam}
                duplicateSchedule={duplicateSchedule}
            />

            <ModalConfirmClear
                modal={modalConfirmClear}
                setModal={setModalConfirmClear}
                selectedTeam={selectedTeam}
                clearSchedule={clearSchedule}
            />

        </Container>
    );
};

export default ShiftSchedulerGrid;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const ControlPanel = styled.div`
    display: flex;
    align-items: center;
    gap: 50px;
`;

const FlexContainer = styled.div<{ gap?: number }>`
	display: flex;
	gap: ${p => (p.gap || 10) + 'px'};
`;

const SpaceBetweenContainer = styled.div`
    display: flex;
    justify-content: space-between;
`;

const Icon = styled.i`
	color: white;
	font-size: 14px;
`;

const WeekDatePicker = styled(Input)`
	width: 150px;
`;

const ScrollableTableBody = styled.div`
  height: 400px; /* Adjust height as needed */
  overflow-y: auto;
  overflow-x: hidden;
`;
